import { Box, Card, CardBody, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { BiMenu } from 'react-icons/bi';
import { RiBook2Line } from 'react-icons/ri';

import { PortfolioDrawer, usePortfolioDrawerControls } from '@/cip/Portfolio/PortfolioDrawer/PortfolioDrawer';

import { ResourceType, Withdrawal } from '../_openapi/portfolio-optimizer';
import { useWithdrawals } from '../api/withdrawal';
import { PageHeader } from '../widgets/PageHeader';
import { AsOfDateTag } from '../widgets/Table/AsOfDateTag';
import { CreateWidgetFromTable } from '../widgets/Table/CreateWidgetFromTable';
import { Pagination } from '../widgets/Table/Pagination';
import { Table } from '../widgets/Table/Table';
import { TableControls } from '../widgets/Table/TableControls';
import { usePersistentTableState } from '../widgets/Table/usePersistentTableState';
import { useTableColumnSettings } from '../widgets/Table/useTableColumnSettings';
import { withdrawalListColumns } from './withdrawalListColumns';

const columnHelper = createColumnHelper<Withdrawal>();
export const WithdrawalList = () => {
    const { viewPortfolio, ...portfolioDrawerProps } = usePortfolioDrawerControls();
    const { pagination, setPagination, sorting, setSorting, resetTable, columnFilters, setColumnFilters } =
        usePersistentTableState(withdrawalListColumns);

    const { data: withdrawalsPage, isFetching } = useWithdrawals({ pagination, columnFilters, sorting });

    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } =
        useTableColumnSettings('withdrawals');

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: 'contextMenu',
                cell: ({ row }) => {
                    return (
                        <Menu isLazy>
                            <MenuButton
                                size="xs"
                                as={IconButton}
                                aria-label="Options"
                                icon={<Icon boxSize={3} as={BiMenu} />}
                                w="20px"
                                minW="20px"
                                h="20px"
                                variant="outline"
                                borderRadius="sm"
                            />
                            <Portal>
                                <MenuList>
                                    <MenuItem
                                        icon={<Icon boxSize={4} as={RiBook2Line} />}
                                        onClick={() => viewPortfolio(row.original.pId)}
                                    >
                                        Portfolio details
                                    </MenuItem>
                                </MenuList>
                            </Portal>
                        </Menu>
                    );
                },
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                    isSticky: true,
                },
            }),
            ...withdrawalListColumns,
        ],
        [viewPortfolio],
    );

    const table = useReactTable({
        data: withdrawalsPage?.withdrawals || [],
        columns,
        state: {
            columnVisibility,
            columnOrder,
            pagination,
            sorting,
            columnFilters,
        },
        initialState: {
            columnPinning: {
                left: ['contextMenu'],
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <>
            <PageHeader title="Withdrawals">
                <AsOfDateTag dateString={withdrawalsPage?.asOfDate} />
            </PageHeader>
            <Card>
                <CardBody>
                    <TableControls table={table} onReset={resetTable} resourceType={ResourceType.WITHDRAWAL}>
                        <Box ml="auto">
                            <CreateWidgetFromTable
                                initialValue={{
                                    type: 'withdrawal',
                                    filters: table.getState().columnFilters,
                                    sort: table.getState().sorting,
                                }}
                            />
                        </Box>
                    </TableControls>
                    <Table table={table} isLoading={isFetching} allowXOverflow />
                    <Pagination table={table} totalItems={withdrawalsPage?.totalNr} />
                </CardBody>
            </Card>
            <PortfolioDrawer {...portfolioDrawerProps} />
        </>
    );
};
